import { Injectable } from '@angular/core';

import { Sitecore } from '@frontend/sports/common/client-config-data-access';
import { StringInterpolationPipe } from '@frontend/sports/common/core/utils/string-interpolation';
import { OddsOperations } from '@frontend/sports/odds/feature';
import { NativePrice } from '@frontend/sports/odds/feature/native-price';
import { LoadingIndicatorService } from '@frontend/vanilla/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Action, Store } from '@ngrx/store';
import { last } from 'lodash-es';
import { concatMap, finalize, map, mergeMap, of } from 'rxjs';

import {
    ApiKey,
    BetBuilderPriceInfo,
    BetBuilderPriceService,
    PriceRequest,
    Status,
} from '../../../lib/bet-builder/services/sportcast/bet-builder-price.service';
import { betslipSelector } from '../../base/store/selectors';
import { IBetslipRootState } from '../../base/store/state';
import { BetslipBetBuilderPick } from '../../core/picks/betslip-bet-builder-pick';
import { PickUpdateType, PriceType } from '../../core/picks/pick-models';
import { createSignedName } from '../../core/picks/signed-name.model';
import { BetslipNotificationActions } from '../notifications/betslip-notification.actions';
import BetslipPickActions from '../picks/actions';
import BetslipErrorsActions from '../validation/actions';
import { BetBuilderBetslipNotification } from '../validation/errors/betslip-notification';
import BetbuilderActions from './actions';

@Injectable()
export default class BetBuilderEffects {
    constructor(
        private actions$: Actions,
        private store: Store<IBetslipRootState>,
        private betBuilderPriceProvider: BetBuilderPriceService,
        private loadingIndicatorService: LoadingIndicatorService,
        private interpolationPipe: StringInterpolationPipe,
        private sitecore: Sitecore,
    ) {}

    requestBetBuilderPriceEffect$ = createEffect(() => {
        const effect = this;

        return this.actions$.pipe(
            ofType(BetbuilderActions.requestBetBuilderPrice),
            concatMap((action) => {
                if (action.payload.sportcastOptions.length === 1 && !action.payload.isInit) {
                    const payload = {
                        payload: {
                            pickId: action.payload.pickId,
                            eventId: action.payload.eventId,
                            sportId: action.payload.sportId,
                            sportcastOptions: action.payload.sportcastOptions,
                            price: NativePrice.empty(),
                        },
                    };

                    return of(BetbuilderActions.updateBetBuilderPick(payload));
                }

                const priceRequest: PriceRequest = {
                    bets: action.payload.sportcastOptions.map((so) => so.longId),
                    externalFixtureId: action.payload.externalFixtureId,
                    sportId: action.payload.sportId,
                    stake: action.payload.stake,
                    key: action.payload.useV2Key ? ApiKey.Sportcast : ApiKey.BetGenius,
                };

                const loadingIndicator = this.loadingIndicatorService.start({ delay: 0 });

                return effect.betBuilderPriceProvider.requestPrice(priceRequest).pipe(
                    mergeMap(function* (priceResponse: BetBuilderPriceInfo): Iterable<Action> {
                        if (priceResponse.statusCode !== Status.OK && action.payload.isInit) {
                            yield BetslipPickActions.removePick({ pickId: action.payload.pickId });

                            return;
                        } else if (action.payload.sportcastOptions.length === 1) {
                            return;
                        }
                        if (priceResponse.isInfinity) {
                            // infinity price indicates that the combination is impossible, and redundant selection may be any one of the two selected, otherwise has to be the last one
                            const hasRedundantSelections = priceResponse.redundantSelections?.length && action.payload.sportcastOptions?.length === 2;
                            const sportcastOption = hasRedundantSelections
                                ? action.payload.sportcastOptions.find((option) => option.longId === priceResponse.redundantSelections[0].longId)!
                                : last(action.payload.sportcastOptions)!;

                            if (hasRedundantSelections) {
                                const pickPayload = {
                                    payload: {
                                        pickId: action.payload.pickId,
                                        eventId: action.payload.eventId,
                                        sportId: action.payload.sportId,
                                        sportcastOptions: action.payload.sportcastOptions,
                                        price: priceResponse.price || NativePrice.empty(),
                                    },
                                };

                                yield BetbuilderActions.updateBetBuilderPick(pickPayload);
                            }

                            yield BetbuilderActions.removeIncorrectSelection({
                                sportcastOption,
                                eventId: action.payload.eventId,
                                pickId: action.payload.pickId,
                            });

                            return;
                        }

                        const price = priceResponse.price || NativePrice.empty();

                        const payload = {
                            payload: {
                                pickId: action.payload.pickId,
                                eventId: action.payload.eventId,
                                sportId: action.payload.sportId,
                                sportcastOptions: action.payload.sportcastOptions,
                                price,
                            },
                        };

                        yield BetbuilderActions.updateBetBuilderPick(payload);

                        for (const redundantSelection of priceResponse.redundantSelections) {
                            yield BetbuilderActions.removeIncorrectSelection({
                                sportcastOption: action.payload.sportcastOptions.find((option) => option.longId === redundantSelection.longId)!,
                                eventId: action.payload.eventId,
                                pickId: action.payload.pickId,
                            });
                        }
                    }),
                    finalize(() => loadingIndicator.done()),
                );
            }),
        );
    });

    handleUpdateBetBuilderPickEffect$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(BetbuilderActions.updateBetBuilderPick),
            concatLatestFrom(() => this.store.select(betslipSelector)),
            mergeMap(([action, betslipState]) => {
                const existingBetBuilderPickFromEntainUi = betslipState.picks.pickList.find(
                    (p) => BetslipBetBuilderPick.isEntainUiBetBuilderPick(p) && p.eventId === action.payload.eventId,
                ) as BetslipBetBuilderPick;
                existingBetBuilderPickFromEntainUi.sportcastOptions = action.payload.sportcastOptions;
                existingBetBuilderPickFromEntainUi.market.name = createSignedName(action.payload.sportcastOptions.map((o) => o.name).join(';'));
                existingBetBuilderPickFromEntainUi.option.prices = [
                    {
                        id: 0,
                        isVisible: true,
                        marketId: existingBetBuilderPickFromEntainUi.market.id,
                        nativeOdds: OddsOperations.createOdds(action.payload.price.toJSON()),
                        type: PriceType.Fixed,
                    },
                ];

                return [
                    BetslipPickActions.updateV2Picks({
                        pickUpdates: [{ pick: existingBetBuilderPickFromEntainUi, updateType: PickUpdateType.Odds }],
                    }),
                    //acceptCurrentPrice is required to update odds in betslip - updateV2Pick triggers the acceptance ONLY if 'oddsAcceptanceMode' is 'any' or 'higher'
                    BetslipPickActions.acceptCurrentPrice({
                        pickIds: [existingBetBuilderPickFromEntainUi.id],
                        isManuallyAccepted: true,
                    }),
                ];
            }),
        );
    });

    removeIncorrectSelectionEffect$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(BetbuilderActions.removeIncorrectSelection),
            mergeMap((action) => {
                if (this.sitecore.betBuilderEntainUI.messages) {
                    return [
                        BetslipPickActions.requestRemoveEntainUiBetBuilderPick({
                            pickId: action.pickId,
                            longId: action.sportcastOption.longId,
                            eventId: action.eventId,
                        }),
                        BetbuilderActions.displayErrorMessage({
                            message: this.interpolationPipe.transform(this.sitecore.betBuilderEntainUI.messages.RemovedLegMessage, {
                                leg: action.sportcastOption.name || this.sitecore.betBuilderEntainUI.messages.LastLeg,
                            }),
                        }),
                    ];
                } else {
                    return [];
                }
            }),
        );
    });

    displayErrorMessageEffect$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(BetbuilderActions.displayErrorMessage),
            map((action) => BetslipNotificationActions.setMessage({ payload: new BetBuilderBetslipNotification(action.message) })),
        );
    });

    validateBetSlipOnBetBuilderRemoval$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(BetbuilderActions.removePick),
            map(() => BetslipErrorsActions.validateBetslip()),
        );
    });
}
